import { getPlanDetails } from '../planDetails';
import { getCompanyFixture, getSponsorFixture } from './fixtures';

export const isMikasaUrl = (url) =>
  /\/(corporate-profiles|plans\/signup|customer-profiles|onboarding)(\/|$|\?)/.test(String(url));

const jsonResponse = (body, status = 200) =>
  new Response(JSON.stringify(body), {
    status,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  });

const matchSegment = (url, pattern) => {
  const match = String(url).match(pattern);
  return match ? decodeURIComponent(match[1]) : null;
};

const parseBody = (init = {}) => {
  if (!init.body) return {};
  if (typeof init.body === 'string') {
    try {
      return JSON.parse(init.body);
    } catch (error) {
      return {};
    }
  }
  return init.body;
};

export const buildCorporateProfile = (companyId) => {
  const fixture = getCompanyFixture(companyId);
  const offices = fixture.offices.map((name, index) => ({
    id: `${companyId}-office-${index + 1}`,
    type: 'office-profiles',
    attributes: {
      name,
      'office-name': name
    }
  }));

  return {
    data: {
      id: companyId,
      type: 'corporate-profiles',
      attributes: {
        name: fixture.name,
        'company-name': fixture.name
      }
    },
    included: offices
  };
};

export const buildPlanPayload = (planCode) => {
  const details = getPlanDetails(planCode);
  if (!details) {
    return null;
  }

  return {
    data: {
      id: planCode,
      type: 'plans',
      attributes: {
        code: planCode,
        'plan-code': planCode,
        name: details.planName
      }
    }
  };
};

export const buildSponsorProfile = (sponsorId) => {
  const fixture = getSponsorFixture(sponsorId);
  return {
    data: {
      id: sponsorId,
      type: 'customer-profiles',
      attributes: {
        'first-name': fixture.firstName,
        'last-name': fixture.lastName,
        'photo-uri': fixture.photoUri,
        'corporation-name': fixture.corporationName,
        nmls: fixture.nmlsId,
        'freemium-agents': fixture.freemiumAgents
      }
    }
  };
};

export const buildOnboardingResult = (signupData = {}) => {
  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'http://localhost:8090';

  return {
    transaction_id: `mock-txn-${signupData.plan_code || 'signup'}`,
    auth_callback_url: `${origin}/dev/homebot#access_token=mock-access-token`,
    mock: true,
    signup: signupData
  };
};

export const handleMikasaRequest = (url, init = {}) => {
  const method = (init.method || 'GET').toUpperCase();
  const href = String(url);

  const companyId = matchSegment(href, /\/corporate-profiles\/([^/?#]+)/);
  if (companyId && method === 'GET') {
    return jsonResponse(buildCorporateProfile(companyId));
  }

  const planCode = matchSegment(href, /\/plans\/signup\/([^/?#]+)/);
  if (planCode && method === 'GET') {
    const payload = buildPlanPayload(planCode);
    if (!payload) {
      return jsonResponse({ errors: { base: [`Unknown plan ${planCode}`] } }, 404);
    }
    return jsonResponse(payload);
  }

  const sponsorId = matchSegment(href, /\/customer-profiles\/([^/?#]+)/);
  if (sponsorId && method === 'GET') {
    return jsonResponse(buildSponsorProfile(sponsorId));
  }

  if (/\/onboarding\/goal(\/|$|\?)/.test(href) && method === 'PUT') {
    return jsonResponse({ ok: true, goal: parseBody(init).goal });
  }

  if (/\/onboarding(\/|$|\?)/.test(href) && method === 'POST') {
    return jsonResponse(buildOnboardingResult(parseBody(init)), 201);
  }

  return jsonResponse({ errors: { base: [`No mock for ${method} ${href}`] } }, 404);
};
